import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppSelector } from '../../../hooks/hooks';
import TiminatorTableSettings, { TiminatorTableColumn } from '../../../components/shared/table-v2/types';
import { Lx } from '../../../i18n/consts';
import { StatusTag } from '../../../components/shared/status-tag';
import { DeleteIconButton } from '../../../components/shared/delete-icon-button';
import { Client } from '../../../features/clients/types';
import { normalizeString } from '../../../utils/strings';
import { buildSearchValues, filterByStatusAndSearch, getStatusMessageId, getStatusSortValue, getStatusValue } from '../../../utils/table-filters';
import styles from '../clients.module.scss';

interface UseClientsTableOptions {
    search: string;
    selectedStatus: number;
    isMobile: boolean;
    canDelete: boolean;
    onDeleteClick?: (client: Client) => void;
}

export const useClientsTable = ({
    search,
    selectedStatus,
    isMobile,
    canDelete,
    onDeleteClick,
}: UseClientsTableOptions): TiminatorTableSettings<Client> => {
    const intl = useIntl();
    const clients = useAppSelector(store => store.clients.items);

    const handleDeleteClick = useCallback((client: Client) => {
        onDeleteClick?.(client);
    }, [onDeleteClick]);

    const renderStatusCell = useCallback((rowData: Client) => (
        <StatusTag status={rowData.isActive ? 'active' : 'suspended'}>
            <span>
                <FormattedMessage id={getStatusMessageId(rowData.isActive)} />
            </span>
        </StatusTag>
    ), []);

    const renderDeleteButton = useCallback((rowData: Client) => (
        <DeleteIconButton onClick={() => handleDeleteClick(rowData)} />
    ), [handleDeleteClick]);

    const columns = useMemo((): TiminatorTableColumn<Client>[] => {
        const createTextColumn = (
            id: string,
            labelId: string,
            className: string,
            valueSelector: (row: Client) => React.ReactNode,
            sortValueSelector: (row: Client) => string,
        ): TiminatorTableColumn<Client> => ({
            id,
            label: intl.formatMessage({ id: labelId }),
            width: '1fr',
            sortable: true,
            getSortValue: (rowData: Client) => normalizeString(sortValueSelector(rowData)),
            format: (rowData: Client) => (
                <span className={className}>{valueSelector(rowData)}</span>
            ),
        });

        const createStatusColumn = (overrides?: Partial<TiminatorTableColumn<Client>>): TiminatorTableColumn<Client> => ({
            id: 'status',
            label: intl.formatMessage({ id: Lx.General.STATUS }),
            align: 'left',
            width: '160px',
            format: renderStatusCell,
            sortable: true,
            getSortValue: (rowData: Client) => getStatusSortValue(rowData.isActive),
            ...overrides,
        });

        const createDeleteColumn = (overrides?: Partial<TiminatorTableColumn<Client>>): TiminatorTableColumn<Client> => ({
            id: 'deleteButton',
            align: 'right',
            width: '64px',
            format: renderDeleteButton,
            ...overrides,
        });

        if (isMobile) {
            return [
                {
                    id: 'mobileInfo',
                    label: intl.formatMessage({ id: Lx.General.NAME }),
                    width: '1fr',
                    format: (rowData: Client) => (
                        <div className={styles.mobileInfo}>
                            <span className={styles.textDesktopPrimary}>{rowData.name}</span>
                            <span className={styles.textMobileContactPerson}>{rowData.contactPerson}</span>
                            <span className={styles.textMobileEmail}>{rowData.email}</span>
                            <span className={styles.textMobileAccent}>{rowData.site}</span>
                        </div>
                    ),
                },
                createStatusColumn({
                    align: 'right',
                    width: '25%',
                }),
                ...(canDelete ? [createDeleteColumn({
                    width: '24px',
                })] : []),
            ];
        }

        return [
            createTextColumn('name', Lx.General.NAME, styles.textDesktopPrimaryWrap, client => client.name, client => client.name ?? ''),
            createTextColumn(
                'contactPerson',
                Lx.Client.CONTACT_PERSON,
                styles.textDesktopSecondaryWrap,
                client => client.contactPerson,
                client => client.contactPerson ?? '',
            ),
            createTextColumn('email', Lx.General.EMAIL, styles.textDesktopAccent, client => client.email, client => client.email ?? ''),
            createTextColumn('site', Lx.Client.SITE, styles.textDesktopAccent, client => client.site, client => client.site ?? ''),
            createStatusColumn(),
            ...(canDelete ? [createDeleteColumn()] : []),
        ];
    }, [canDelete, intl, isMobile, renderDeleteButton, renderStatusCell]);

    const data = useMemo(() => (
        filterByStatusAndSearch(
            clients,
            selectedStatus,
            client => client.isActive,
            search,
            client => buildSearchValues([
                client.name,
                client.contactPerson,
                client.email,
                client.site,
                getStatusValue(intl, client.isActive),
            ])
        )
    ), [clients, search, selectedStatus, intl]);

    const isEmpty = clients.length === 0;

    return {
        columns,
        data,
        isEmpty,
    };
};
